<template>
    <Dialog
        :visible="visible"
        @update:visible="updateVisible"
        header="Upload por IA"
        @show="onShow"
        @hide="onHide"
        class="w-11 lg:w-6"
        modal
    >
        <form @submit.prevent="onSubmit">
            <div class="w-full">
                <!-- Campo Contrato -->
                <div class="field mt-4">
                    <InputGroup>
                        <FloatLabel>
                            <InputText id="contrato_id" v-model="form.contrato" class="w-full" readonly />
                            <label for="contrato_id">Contrato<span class="text-red-500">*</span></label>
                        </FloatLabel>
                        <Button icon="pi pi-search" severity="info" @click="visibleContratoListaSelect = true"></Button>
                        <Button icon="pi pi-times" severity="danger" @click="clearContrato"></Button>
                    </InputGroup>
                    <div class="text-xs text-red-500">{{ errors['contrato_id'] }}</div>
                </div>

                <!-- Upload de Arquivo -->
                <Fieldset legend="Upload de Arquivo">
                    <!-- Toolbar com Botão -->
                    
                    <BlockUI :blocked="isLoading" class="relative w-full px">
                        <Toolbar>
                            <template #end>
                                <div class="flex gap-1">
                                    <Button 
                                        label="Selecionar Arquivo"
                                        icon="pi pi-folder-open"
                                        severity="primary"
                                        @click="openFileDialog"
                                    />
                                    <Button 
                                        label="Limpar"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        @click="clearFileDialog"
                                    />
                                </div>
                            </template>
                        </Toolbar>

                        <div class="field mt-4">
                            <FilePond
                                ref="pond"
                                name="arquivo[]"
                                labelIdle='Arraste o arquivo ou <span class="filepond--label-action">clique aqui</span>'
                                acceptedFileTypes="application/pdf"
                                :files="files"
                                @updatefiles="onFileUpdate"
                                :allowMultiple="true"
                                :instantUpload="false"
                            />
                        </div>

                        <div v-if="isLoading" class="absolute" style="top: calc(50% - 20px); left: calc(50% - 20px);">
                            <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                            <!-- <i class="pi pi-spinner pi-spin text-4xl text-blue-100"></i> -->
                        </div>
                    </BlockUI>
                </Fieldset>
            </div>

            <!-- Botões -->
            <div class="flex justify-content-end gap-2 mt-4">
                <Button
                    label="Enviar"
                    icon="pi pi-upload"
                    severity="success"
                    :loading="isLoading"
                    @click="handleUpload"
                />
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    severity="warn"
                    @click="updateVisible(false)"
                />
            </div>
        </form>

        <ContratoListaSelect
            :visible="visibleContratoListaSelect"
            :permissoes="permissoes"
            @close="visibleContratoListaSelect = false"
            @select="selectContrato"
        />

        <ContaListaDialog
            :visible="visibleContaDialog"
            :error="error"
            :contas="contas"
            @close="visibleContaDialog = false"
        />
    </Dialog>
</template>

<script>
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import Fieldset from 'primevue/fieldset';
import Toolbar from 'primevue/toolbar';
import BlockUI from 'primevue/blockui';
import ProgressSpinner from 'primevue/progressspinner';

import 'filepond/dist/filepond.min.css'

import Dialog from 'primevue/dialog'

import InputGroup from 'primevue/inputgroup'
import FloatLabel from 'primevue/floatlabel'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'

import ContratoListaSelect from '@/page/contrato/ListaSelect.vue'
import Arquivo from '@/service/arquivo.js'
import ContaListaDialog from '@/page/conta/ListaDialog.vue';

const FilePond = vueFilePond(FilePondPluginFileValidateType)

export default {
    components: {
        Dialog,
        InputGroup,
        FloatLabel,
        InputText,
        Button,
        Fieldset,
        FilePond,
        ContratoListaSelect,
        ContaListaDialog,

        Toolbar,
        ProgressSpinner,
        BlockUI
    },
    props: {
        visible: Boolean,
        data: Object,
        permissoes: Object,
    },
    emits: ['save', 'close'],
    data() {
        return {
            form: {},
            errors: {},
            files: [],
            
            isLoading: false,
            visibleContratoListaSelect: false,

            error: null,
            contas: [],
            visibleContaDialog: false,
        }
    },
    methods: {
        onFileUpdate(files) {
            this.files = files.map(file => file.file)
        },
        openFileDialog() {
            this.$refs.pond.browse()
        },
        clearFileDialog() {
            this.files = []
        },

        async handleUpload() {
            this.errors = {}

            if (!this.form.contrato_id) {
                this.errors.contrato_id = "Campo obrigatório"
                return
            }

            if (this.files.length === 0) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Nenhum arquivo selecionado',
                    life: 5000
                })
                return
            }

            this.isLoading = true

            const formData = new FormData()
            this.files.forEach(file => {
                formData.append('arquivo[]', file)
            })

            try {
                const response = await Arquivo.contaCarne(this.form.contrato_id, formData)

                this.files = []
                this.form = {}

                this.$toast.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: `Foram inseridos ${response.data.length} novos boletos.`,
                    life: 8000
                })

                this.$emit('save', response.data)
                this.$emit('close')
            } catch (error) {
                if (error.response?.data?.erros) {
                    this.errors = error.response.data.erros
                } else if (error.response?.data?.msg) {
                    if (error.response.data.contas) {
                        this.error = error.response.data.msg;
                        this.contas = error.response.data.contas;
                        this.visibleContaDialog  = true;
                        return;
                    }
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: error.response.data.msg,
                        life: 10000
                    });
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Erro ao realizar upload',
                        life: 5000
                    })
                }
            } finally {
                this.isLoading = false
            }
        },

        selectContrato(contrato) {
            this.form.contrato = contrato?.id_primario || null
            this.form.contrato_id = contrato?.id || null
        },

        clearContrato() {
            this.form.contrato = null
            this.form.contrato_id = null
        },

        updateVisible(visible) {
            this.$emit('close', visible)
        },

        onShow() {
            // Pode adicionar lógica ao abrir o diálogo, se necessário
            this.form = { ...this.data };
        },
        onHide() {
            this.form = {};
            this.files = [];
            // this.$refs.pond.removeFiles();
        }
    }
}
</script>

<style scoped>
.field {
    margin-bottom: 1rem;
}
</style>
